import React, { useState } from 'react';
import { XCircle, User, Mail, AlertCircle } from 'lucide-react';
import { DeleteRequest } from '../types';
import { Modal } from './Modal';

interface RejectDeleteRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: DeleteRequest | null;
  onConfirm: (reason: string) => void;
  loading?: boolean;
}

const REJECTION_REASON_SUGGESTIONS = [
  'Insufficient information provided',
  'Active bookings or pending transactions',
  'Account verification required',
  'Outstanding payments or disputes',
  'Request does not meet deletion criteria',
  'Additional documentation needed',
  'Account suspension recommended instead',
  'Please contact support for alternative solutions',
];

export const RejectDeleteRequestModal: React.FC<RejectDeleteRequestModalProps> = ({
  isOpen,
  onClose,
  request,
  onConfirm,
  loading = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  const handleConfirm = () => {
    if (reason.trim().length < 10) {
      setError('Rejection reason must be at least 10 characters');
      return;
    }

    setError('');
    onConfirm(reason.trim());
  };

  const handleSuggestionClick = (suggestion: string) => {
    setReason(suggestion);
    setError('');
  };

  if (!request) return null;

  const characterCount = reason.length;
  const isValid = characterCount >= 10;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Reject Delete Request">
      <div className="space-y-6">
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900 mb-1">
                Rejection Notice
              </h3>
              <p className="text-sm text-amber-800">
                You are about to reject this account deletion request. The user will be notified with the reason you provide.
              </p>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Request Details:</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-700">
                <span className="font-medium">Name:</span> {request.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-700">
                <span className="font-medium">Email:</span> {request.email}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-700">
                <span className="font-medium">Account Type:</span> {request.accountType === 'provider' ? 'Provider' : 'Service User'}
              </span>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
          <h4 className="text-sm font-semibold text-slate-900 mb-2">User's Deletion Reason:</h4>
          <p className="text-sm text-slate-700 italic">{request.reason}</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            Rejection Reason <span className="text-red-600">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError('');
            }}
            placeholder="Provide a clear reason for rejecting this deletion request..."
            rows={5}
            maxLength={500}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
              error ? 'border-red-300 bg-red-50' : 'border-slate-300'
            }`}
          />
          <div className="flex items-center justify-between mt-2">
            <div>
              {error && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <XCircle className="h-4 w-4" />
                  {error}
                </p>
              )}
            </div>
            <p className={`text-sm ${isValid ? 'text-green-600' : 'text-slate-500'}`}>
              {characterCount}/500 characters {isValid && '✓'}
            </p>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Quick Suggestions:</h4>
          <div className="flex flex-wrap gap-2">
            {REJECTION_REASON_SUGGESTIONS.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors border border-slate-300"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">Note:</span> Please provide a clear and professional reason for rejection. The user will receive this explanation in their notification.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-6 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid || loading}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                Reject Request
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
